// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { Alert, Spinner } from '@loa/ui-components';
import type { MaxFundingScenarioOutputs } from '../../../../types/calculationResult';
import { formatCurrencyEUR } from './maxFundingDefaults';

type MaxFundingResultsPanelProps = Readonly<{
  outputs: MaxFundingScenarioOutputs | null;
  warnings: string[];
  isLoading: boolean;
  error: string | null;
}>;

interface Row {
  label: string;
  key: keyof MaxFundingScenarioOutputs['upliftedScale'];
  sectionHeader?: string;
}

const ROWS: Row[] = [
  { label: 'Revenue Basis', key: 'maxPensionAtNRA', sectionHeader: 'Revenue Basis' },
  { label: 'Maximum Pension at Normal Retirement Age', key: 'maxPensionAtNRA' },
  { label: 'Maximum Retirement Fund', key: 'maxRetirementFund' },
  { label: 'Future Ordinary Contributions', key: 'maxOrdinaryContribution', sectionHeader: 'Future Ordinary Contributions' },
  { label: 'Maximum Ordinary Contribution', key: 'maxOrdinaryContribution' },
  { label: 'Special Contribution for Past Service', key: 'maxSpecialContribution', sectionHeader: 'Special Contribution for Past Service' },
  { label: 'Maximum Special Contribution', key: 'maxSpecialContribution' },
  { label: 'Plus, Reduced Maximum Ordinary Contribution', key: 'reducedMaxOrdinary' },
  { label: 'Matching Ordinary and Special Contributions', key: 'matchingOrdinary', sectionHeader: 'Matching Ordinary and Special Contributions' },
  { label: 'Ordinary Contribution', key: 'matchingOrdinary' },
  { label: 'Special Contribution', key: 'matchingSpecial' },
];

export function MaxFundingResultsPanel({
  outputs,
  warnings,
  isLoading,
  error,
}: MaxFundingResultsPanelProps) {
  return (
    <div
      className="flex flex-col gap-md py-lg bg-white rounded-card shadow-[0_0_10px_0_rgba(0,0,0,0.10)]"
      data-testid="max-funding-results-panel"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-body-01 font-medium text-black font-sans m-0 px-md">
          Results
        </h3>
        {isLoading && <Spinner size="sm" />}
      </div>

      {warnings.length > 0 && (
        <Alert variant="warning" header="Standard Fund Threshold" data-testid="sft-warning-banner">
          <ul className="m-0 pl-[20px]">
            {warnings.map((w) => (
              <li key={w} className="text-body-03">
                {w}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {error && (
        <Alert variant="error" header="Calculation Error">
          <p>{error}</p>
        </Alert>
      )}

      <div className="overflow-hidden border-x border-[#E5E5EA]">
        <table className="w-full border-collapse">
          <tbody>
            {ROWS.map((row) => {
              if (row.sectionHeader) {
                return (
                  <tr key={`section-${row.sectionHeader}`} className="bg-lilac-05 h-[56px]">
                    <td className="px-md py-[10px] text-body-03 h-[56px] font-medium text-black font-sans">
                      {row.sectionHeader}
                    </td>
                    <td className="px-md py-[10px] text-body-03 h-[56px] font-medium text-black font-sans">
                      Uplifted Scale
                    </td>
                    <td className="px-md py-[10px] text-body-03 h-[56px] font-medium text-black font-sans">
                      N/60ths Basis
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={`row-${row.key}`} className="border-t border-[#E5E5EA]">
                  <td className="px-md py-[12px] h-[56px] overflow-hidden text-ellipsis whitespace-nowrap text-black text-body-03 font-normal font-sans">
                    {row.label}
                  </td>
                  <td
                    className="px-md py-[12px] h-[56px] overflow-hidden text-ellipsis whitespace-nowrap text-black text-body-03 font-normal font-sans"
                    data-testid={`results-uplifted-${row.key}`}
                  >
                    {formatCurrencyEUR(outputs?.upliftedScale?.[row.key] ?? null)}
                  </td>
                  <td
                    className="px-md py-[12px] h-[56px] overflow-hidden text-ellipsis whitespace-nowrap text-black text-body-03 font-normal font-sans"
                    data-testid={`results-nsixtieths-${row.key}`}
                  >
                    {formatCurrencyEUR(outputs?.nSixtiethsScale?.[row.key] ?? null)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
